// src/config/plans.js

/**
 * Single source of truth for plan pricing on the client.
 *
 * These values are for DISPLAY ONLY and are the FULL (default-tier) prices.
 * The price actually charged is resolved server-side from `TIER_PRICES` in
 * `functions/src/regionalPricing.ts` — the checkout never sends an amount to
 * the payment API, so a mismatch here shows a wrong price but cannot cause a
 * wrong charge. Keep these equal to `TIER_PRICES.full` anyway.
 *
 * Visitors in discounted regions see their tier's prices through
 * `useRegionalPricing()`, which asks the server; these values are its fallback.
 *
 * Line-up since Oct 2026: Free / 3 months (€39.99, the plan we want most
 * people to buy) / Premium (€149 for 6 months, with personal teacher time). Premium is
 * deliberately much more expensive: next to it the 3-month plan reads as the
 * sensible choice (compromise effect), and the few who do buy it get real
 * value. `weekly` (Aug 2026) and `monthly` (Oct 2026) were retired: they are
 * absent from PLAN_PRICES and PURCHASABLE_PLAN_IDS but kept in the duration and
 * name maps so existing customers still render correctly.
 *
 * There are no struck-through "regular" prices any more: they were never
 * charged, and German law (§11 PAngV) only allows a reduction against the
 * lowest price of the previous 30 days.
 */

export const PLAN_PRICES = {
  quarterly: 39.99,
  premium: 149,
};

/** Plans a user can buy, in the order they should be displayed. */
export const PURCHASABLE_PLAN_IDS = ['quarterly', 'premium'];

/** Access length in days. Mirrors PLAN_DURATION_DAYS in functions/src/index.ts. */
export const PLAN_DURATION_DAYS = {
  weekly: 7, // retired — legacy customers only
  monthly: 30, // retired — legacy customers only
  quarterly: 90,
  premium: 180,
};

/**
 * Teacher time included in a purchase. Mirrors PLAN_PERKS in
 * functions/src/index.ts, which stamps it onto the subscriptions/ doc; the
 * stamped value is what counts. Plans not listed include no teacher time.
 */
export const PLAN_PERKS = {
  premium: { consultations: 5, teacherCorrections: 10 },
};

/**
 * Consultations for subscriptions bought before Oct 2026, which carry no
 * `perks` field. Existing customers keep what they were sold.
 */
export const LEGACY_CONSULTATIONS = { monthly: 1, quarterly: 3 };

/** i18n keys for plan display names, including retired plans. */
export const PLAN_NAME_KEYS = {
  weekly: 'pricing.plans.weekly.name',
  monthly: 'pricing.plans.monthly.name',
  quarterly: 'pricing.lineup.quarterly.name',
  premium: 'pricing.lineup.premium.name',
};

/**
 * 39.99 -> "€39,99", 149 -> "€149" (German convention, matching the de-DE Intl
 * format used at checkout; whole euros drop the ",00").
 */
export const formatPlanPrice = (value) => {
  if (typeof value !== 'number') return null;
  return Number.isInteger(value) ? `€${value}` : `€${value.toFixed(2).replace('.', ',')}`;
};

export const getPlanPrice = (planId) => PLAN_PRICES[planId] ?? null;
export const getPlanPriceLabel = (planId) => formatPlanPrice(PLAN_PRICES[planId]);
export const isPurchasablePlan = (planId) => PURCHASABLE_PLAN_IDS.includes(planId);
